import { useQuery } from '@tanstack/react-query';
import { supabase } from './supabase';
import { useAuthStore } from '@/store/authStore';

// ── Types ───────────────────────────────────────────────────
//
// Mirror supabase/functions/shift-brief/index.ts response shape,
// and mozaic-shared/src/types/models.ts for the canonical copy.
// Kept as a local copy in this file to match the riskScore.ts
// convention — the web app does not reach into mozaic-shared today.

export interface ShiftBriefWeather {
  conditionText: string | null;
  // KI-036: dsr_daily_setups.temperature_celsius actually stores °F.
  // Surfaced as temperatureF here until the rename.
  temperatureF: number | null;
  windSpeedKnots: number | null;
  windDirection: string | null;
  visibilityNm: number | null;
  seaStateBeaufort: number | null;
  source: string | null;        // 'auto' | 'manual'
  fetchedAt: string | null;
}

export interface ShiftBriefZone1 {
  date: string;
  shift: string;
  projectName: string;
  locationName: string | null;
  ssrName: string | null;
  weather: ShiftBriefWeather | null;
  gps: { lat: number; lon: number } | null;
  sunrise: string | null;
  sunset: string | null;
  severeWeatherFlag: boolean;
}

export interface ShiftBriefHitchFlag {
  personName: string;
  hitchDays: number;
  flag: 'info' | 'warning';
}

export interface ShiftBriefZone2 {
  pobCount: number | null;
  sseCount: number | null;
  ssePct: number | null;
  supervisionCoverageProxy: {
    crewsWithSupervisor: number;
    totalCrews: number;
    percent: number;
  };
  hitchFlags: ShiftBriefHitchFlag[];
}

export interface ShiftBriefForecastedActivity {
  id: string;
  description: string;
  anticipatedRisk: string;
  tags: string[];
}

export interface ShiftBriefZone3 {
  forecastedActivities: ShiftBriefForecastedActivity[];
  riskTagsSummary: Record<string, number>;
  activePermitsCount: Record<string, number>;
  sifFlag: boolean;
  simopsFlag: boolean;
}

export interface ShiftBriefZone4IncompleteActivity {
  id: string;
  description: string;
  priorDsrId: string;
  priorDsrDate: string;
}

export interface ShiftBriefZone4OpenSwaEvent {
  id: string;
  hazardDescription: string;
  workStatus: 'stopped' | 'ongoing';
  priorDsrId: string;
  priorDsrDate: string;
}

export interface ShiftBriefZone4UnresolvedSafetyEvent {
  id: string;
  eventType: string;
  eventTitle: string;
  priorDsrId: string;
  priorDsrDate: string;
}

export interface ShiftBriefZone4 {
  lookbackDsrCount: number;
  incompleteActivities: ShiftBriefZone4IncompleteActivity[];
  incompleteActivitiesCount: number;
  openSwaEvents: ShiftBriefZone4OpenSwaEvent[];
  openSwaEventsCount: number;
  unresolvedSafetyEvents: ShiftBriefZone4UnresolvedSafetyEvent[];
  unresolvedSafetyEventsCount: number;
  handoverNotes: string | null;
}

export type ShiftBriefAttentionSeverity = 'info' | 'warning' | 'critical';

export interface ShiftBriefAttentionItem {
  severity: ShiftBriefAttentionSeverity;
  code: string;
  message: string;
  sourceZone: 1 | 2 | 3 | 4;
}

export interface ShiftBriefZone5 {
  attentionItems: ShiftBriefAttentionItem[];
}

export interface ShiftBriefResponse {
  dsrId: string;
  generatedAt: string;
  zone1: ShiftBriefZone1;
  zone2: ShiftBriefZone2;
  zone3: ShiftBriefZone3;
  zone4: ShiftBriefZone4;
  zone5: ShiftBriefZone5;
  signals: string[];
}

// ── Edge Function invocation ────────────────────────────────

export async function fetchShiftBrief(params: {
  dsrId: string;
  tenantId: string;
}): Promise<ShiftBriefResponse> {
  const { data, error } = await supabase.functions.invoke<ShiftBriefResponse>(
    'shift-brief',
    { body: params },
  );
  if (error) throw new Error(error.message);
  if (!data) throw new Error('shift-brief returned no data');
  return data;
}

// ── Acknowledgment logging ──────────────────────────────────

// Append-only compliance signal. One row per DSR-first-view of the
// Shift Brief; first viewer wins via UNIQUE(dsr_report_id) on
// shift_brief_acknowledgments. Modeled as INSERT ... ON CONFLICT
// DO NOTHING via Supabase upsert with ignoreDuplicates — repeat
// calls silently no-op.
//
// Called from ShiftBriefCard on mount. Fire-and-forget: ack logging
// failing should never block the SSR from reading the brief.
export async function logShiftBriefAcknowledgment(params: {
  dsrId: string;
  tenantId: string;
  userId: string;
}): Promise<void> {
  const { error } = await supabase
    .from('shift_brief_acknowledgments')
    .upsert(
      {
        tenant_id: params.tenantId,
        dsr_report_id: params.dsrId,
        acknowledged_by: params.userId,
      },
      { onConflict: 'dsr_report_id', ignoreDuplicates: true },
    );
  if (error) {
    // Don't throw — log and swallow. The brief view is still useful
    // even if the compliance write fails (RLS glitch, network blip).
    console.error('[shift-brief] ack write failed', error);
  }
}

// ── React Query hook ────────────────────────────────────────

// Cache key: ['shift_brief', dsrId]. Consumers invalidate via
// queryClient.invalidateQueries({ queryKey: ['shift_brief', dsrId] })
// to drive the "Refresh briefing" button. Snapshot-at-load per the
// S76 spec — no window-focus refetch.
export function useShiftBrief(dsrId: string | undefined) {
  const tenantId = useAuthStore((s) => s.tenantId);

  return useQuery({
    queryKey: ['shift_brief', dsrId],
    queryFn: () => fetchShiftBrief({ dsrId: dsrId!, tenantId: tenantId! }),
    enabled: !!dsrId && !!tenantId,
    staleTime: 5 * 60 * 1000,
    refetchOnWindowFocus: false,
  });
}
